// Aviso en el icono de la pestaña (video aprobado "Avisa aunque no estén en tu app").
// - El icono se pinta con el color y el tema elegidos en la Consola (paleta), y se repinta al cambiarlos.
// - Con la pestaña en segundo plano: un circulo rojo con el numero de avisos nuevos (el "(n)" que la campana ya
//   escribe en el titulo) o un telefono verde si esta sonando una llamada. Al volver a la pestaña se limpia.
// - Al desmontar se devuelve el icono original de la pagina.
import { useEffect } from 'react';

import { useApariencia } from '../ui/prefs';
import { alCambiarLlamada, llamadaSonando } from './avisoLlamada';

// "(3) CRM XmarteK" -> 3; "(9+) ..." -> 10 (mas de nueve); sin numero al inicio -> 0.
export function cuentaDelTitulo(titulo: string): number {
  const m = /^\s*\((\d{1,4})(\+)?\)/.exec(titulo ?? '');
  if (!m) return 0;
  return Number(m[1]) + (m[2] ? 1 : 0);
}

export type Insignia = { tipo: 'llamada' } | { tipo: 'cuenta'; texto: string } | null;

// Que mostrar sobre el icono. Solo con la pestaña oculta; la llamada pesa mas que los avisos.
export function insigniaPara({ oculto, cuenta, llamada }: { oculto: boolean; cuenta: number; llamada: boolean }): Insignia {
  if (!oculto) return null;
  if (llamada) return { tipo: 'llamada' };
  if (cuenta > 0) return { tipo: 'cuenta', texto: cuenta > 9 ? '9+' : String(cuenta) };
  return null;
}

const TELEFONO = 'M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2';

function token(nombre: string, respaldo: string) {
  const v = getComputedStyle(document.documentElement).getPropertyValue(nombre).trim();
  return v || respaldo;
}

// Asigna un color al lienzo; si el navegador no entiende el valor (p. ej. oklch en uno viejo) usa el respaldo.
function color(g: CanvasRenderingContext2D, valor: string, respaldo: string) {
  g.fillStyle = '#010203';
  g.fillStyle = valor;
  if (g.fillStyle === '#010203') g.fillStyle = respaldo;
  return g.fillStyle as string;
}

function rectRedondo(g: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  g.beginPath();
  g.moveTo(x + r, y);
  g.arcTo(x + w, y, x + w, y + h, r);
  g.arcTo(x + w, y + h, x, y + h, r);
  g.arcTo(x, y + h, x, y, r);
  g.arcTo(x, y, x + w, y, r);
  g.closePath();
}

// Dibuja el icono de la Consola (onda sobre el color de acento) y la insignia. Devuelve un data URL PNG.
export function dibujarIcono(insignia: Insignia): string | null {
  const c = document.createElement('canvas');
  c.width = c.height = 64;
  let ctx: CanvasRenderingContext2D | null = null;
  try {
    ctx = c.getContext('2d');
  } catch {
    ctx = null;
  }
  if (!ctx) return null;
  const g = ctx;
  const acc = color(g, token('--acc', '#4b45d6'), '#4b45d6');
  const tinta = color(g, token('--on-acc', '#fff'), '#fff');
  g.fillStyle = acc;
  rectRedondo(g, 2, 2, 60, 60, 18);
  g.fill();
  g.strokeStyle = tinta;
  g.lineWidth = 5;
  g.lineCap = 'round';
  g.lineJoin = 'round';
  g.beginPath();
  const onda = [
    [10, 32],
    [18, 32],
    [24, 16],
    [32, 48],
    [40, 24],
    [46, 32],
    [54, 32],
  ];
  onda.forEach(([x, y], i) => (i ? g.lineTo(x, y) : g.moveTo(x, y)));
  g.stroke();
  if (insignia) {
    const cx = 45;
    const cy = 19;
    // Recorte transparente alrededor de la insignia: se lee bien sobre pestañas claras u oscuras.
    g.save();
    g.globalCompositeOperation = 'destination-out';
    g.beginPath();
    g.arc(cx, cy, 21, 0, Math.PI * 2);
    g.fill();
    g.restore();
    g.fillStyle = insignia.tipo === 'llamada' ? color(g, token('--green', '#30a46c'), '#30a46c') : color(g, token('--red', '#e5484d'), '#e5484d');
    g.beginPath();
    g.arc(cx, cy, 18, 0, Math.PI * 2);
    g.fill();
    g.fillStyle = '#fff';
    if (insignia.tipo === 'llamada') {
      g.save();
      g.translate(cx - 11, cy - 11);
      g.scale(22 / 24, 22 / 24);
      g.fill(new Path2D(TELEFONO));
      g.restore();
    } else {
      g.font = `700 ${insignia.texto.length > 1 ? 20 : 25}px system-ui, sans-serif`;
      g.textAlign = 'center';
      g.textBaseline = 'middle';
      g.fillText(insignia.texto, cx, cy + 1.5);
    }
  }
  try {
    return c.toDataURL('image/png');
  } catch {
    return null;
  }
}

let instalado = 0;
let original: { href: string; type: string } | null = null;
let ultimo = '';

function enlace() {
  let l = document.querySelector<HTMLLinkElement>('link[rel~="icon"]');
  if (!l) {
    l = document.createElement('link');
    l.rel = 'icon';
    document.head.append(l);
  }
  return l;
}

// Repinta si algo cambio (o siempre, si cambio la apariencia).
export function repintarFavicon(forzar = false) {
  if (!instalado) return;
  const insignia = insigniaPara({ oculto: document.hidden, cuenta: cuentaDelTitulo(document.title), llamada: llamadaSonando() });
  const clave = `${token('--acc', '')}|${token('--on-acc', '')}|${JSON.stringify(insignia)}`;
  if (!forzar && clave === ultimo) return;
  const url = dibujarIcono(insignia);
  if (!url) return;
  ultimo = clave;
  const l = enlace();
  l.type = 'image/png';
  l.href = url;
}

export function instalarFaviconAviso(): () => void {
  if (typeof document === 'undefined') return () => {};
  instalado++;
  if (instalado === 1) {
    const l = enlace();
    original = { href: l.getAttribute('href') ?? '', type: l.getAttribute('type') ?? '' };
  }
  const cambio = () => repintarFavicon();
  // La campana escribe "(n) ..." en el titulo: lo observamos sin tocarla. (Cambiar el href del icono tambien
  // dispara el observador, pero la clave no cambia y no se repinta.)
  const obs = new MutationObserver(cambio);
  obs.observe(document.head, { subtree: true, childList: true, characterData: true });
  document.addEventListener('visibilitychange', cambio);
  const mq = typeof matchMedia === 'function' ? matchMedia('(prefers-color-scheme: dark)') : null;
  const tema = () => repintarFavicon(true);
  mq?.addEventListener?.('change', tema);
  const sinLlamada = alCambiarLlamada(cambio);
  repintarFavicon(true);
  return () => {
    obs.disconnect();
    document.removeEventListener('visibilitychange', cambio);
    mq?.removeEventListener?.('change', tema);
    sinLlamada();
    instalado--;
    if (!instalado && original) {
      const l = enlace();
      if (original.type) l.type = original.type;
      else l.removeAttribute('type');
      l.href = original.href;
      original = null;
      ultimo = '';
    }
  };
}

// Se monta una vez en el marco de la Consola (Shell).
export function useFaviconAviso() {
  const { tema, paleta } = useApariencia();
  useEffect(() => instalarFaviconAviso(), []);
  useEffect(() => repintarFavicon(true), [tema, paleta]);
}
